import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom'
import { Box, Typography, Button, Paper, Alert, Chip, Link, Tooltip, CircularProgress } from '@mui/material'
import { Sheet } from '../../components'
import { slotDisplayName } from '../photo-upload/PhotoUpload'
import ShotCompare from './ShotCompare'
import PromptDrawer from './PromptDrawer'
import RejectDialog from './RejectDialog'
import ShadowControl from './ShadowControl'
import ColorBrush from './ColorBrush'
import VersionHistory from './VersionHistory'
import ReplaceDialog from './ReplaceDialog'
import DownloadButton from './DownloadButton'
import RepublishNotice from './RepublishNotice'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import FlipIcon from '@mui/icons-material/Flip'
import { slotState, pieceBadges, readyPhotoIds, imageUrl, isRegenerating, downloadTarget, mirrorErrorMessage } from './reviewModel'
import { approve, getAttempts, retryInBatch, reject, removeShot, unapprove, mirrorShot } from './photoReviewApi'

// Previews first; ShotCompare swaps to the full-size pair once the reviewer zooms.
function compareSources(slot) {
    return {
        rawSrc: imageUrl(slot.photoId, 'raw', slot.rawVersion, 'preview'),
        editedSrc: imageUrl(slot.photoId, 'edited', slot.attempts, 'preview'),
        rawFullSrc: imageUrl(slot.photoId, 'raw', slot.rawVersion),
        editedFullSrc: imageUrl(slot.photoId, 'edited', slot.attempts),
    }
}

function FailedError({ photoId, fetchResource }) {
    const [message, setMessage] = useState(null)
    useEffect(() => {
        let cancelled = false
        getAttempts(fetchResource, photoId).then(response => { if (!cancelled) setMessage(response.lastError || null) })
        return () => { cancelled = true }
    }, [photoId, fetchResource])
    return message ? <Alert severity='error' sx={{ mt: 1 }}>{message}</Alert> : null
}

export default function PieceReview({ item, fetchResource, inFlight, onReroll, onReeditWithPrompt, onReshadow, onColorMatch, onChanged, onReopenCancelled }) {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const { sku } = useParams()
    const [confirmAll, setConfirmAll] = useState(false)
    const [promptSlot, setPromptSlot] = useState(null)
    const [brushSlot, setBrushSlot] = useState(null)
    const [replaceSlot, setReplaceSlot] = useState(null)
    // photoId -> why its Download failed, shown on that shot.
    const [downloadErrors, setDownloadErrors] = useState(() => new Map())
    const setDownloadError = (photoId, message) => setDownloadErrors(prev => {
        const next = new Map(prev)
        if (message) next.set(photoId, message)
        else next.delete(photoId)
        return next
    })
    // The shot whose Mirror is running, and why the last one was refused.
    const [mirroring, setMirroring] = useState(null)
    const [mirrorError, setMirrorError] = useState(null)
    const [approving, setApproving] = useState(false)
    const [retrying, setRetrying] = useState(null)
    const [expanded, setExpanded] = useState(() => new Set())
    const [rejectingSlot, setRejectingSlot] = useState(null)
    const [rejectFailed, setRejectFailed] = useState(false)
    const [removingSlot, setRemovingSlot] = useState(null)
    const [removeFailed, setRemoveFailed] = useState(false)
    // The removal request is running: the sheet stays open with its confirm
    // disabled, so a double-click sends one POST.
    const [removing, setRemoving] = useState(false)
    // The shot whose Unapprove is running, and the server's reason when one is refused.
    const [unapproving, setUnapproving] = useState(null)
    const [unapproveError, setUnapproveError] = useState(null)
    // Shots whose edit changed under the reviewer, so their approve was refused.
    const [changedShots, setChangedShots] = useState(() => new Set())
    // Every other approve failure, in the server's words (the call is quiet).
    // A list: Approve all can fail on several shots in one go.
    const [approveErrors, setApproveErrors] = useState([])
    const formatDate = iso => new Date(iso).toLocaleString(i18n.language)
    // The shot the reviewer pressed owns the wheel and drags; every other
    // photo leaves them to the page. A press outside any shot releases it.
    const [activeId, setActiveId] = useState(null)
    useEffect(() => {
        const release = e => { if (!e.target.closest?.('[data-shot-card]')) setActiveId(null) }
        document.addEventListener('pointerdown', release)
        return () => document.removeEventListener('pointerdown', release)
    }, [])
    const toggleExpanded = photoId => setExpanded(prev => {
        const next = new Set(prev)
        if (next.has(photoId)) next.delete(photoId)
        else next.add(photoId)
        return next
    })

    useEffect(() => {
        if (!item) navigate('/photo-review')
    }, [item, navigate])
    if (!item) return null

    const badges = pieceBadges(item)
    // A shot mid re-edit is about to get a new image, so Approve all must not
    // sign off the one it is replacing.
    const isBusy = slot => isRegenerating(inFlight, slot) || mirroring === slot.photoId
    const ready = item.slots.filter(slot => slotState(slot) === 'ready' && !isBusy(slot)).map(slot => slot.photoId)

    async function afterApprove() {
        const items = await onChanged()
        const fresh = items?.find(candidate => candidate.sku === item.sku)
        if (!fresh || readyPhotoIds(fresh).length === 0) navigate('/photo-review')
    }

    // Approves the version on screen. A 409 carrying `attempts` means a
    // re-edit landed since the page loaded: nothing was approved, and the
    // refetch in afterApprove brings the new version up to review.
    async function approveShown(photoId) {
        const slot = item.slots.find(candidate => candidate.photoId === photoId)
        const response = await approve(fetchResource, photoId, slot.attempts)
        const changed = Boolean(response?.error && Number.isInteger(response.attempts))
        if (response?.error && !changed) setApproveErrors(prev => [...prev, response.error])
        setChangedShots(prev => {
            const next = new Set(prev)
            if (changed) next.add(photoId)
            else next.delete(photoId)
            return next
        })
    }

    async function handleApprove(photoId) {
        setApproveErrors([])
        setApproving(true)
        await approveShown(photoId)
        setApproving(false)
        await afterApprove()
    }

    async function handleRetry(photoId) {
        setRetrying(photoId)
        await retryInBatch(fetchResource, photoId)
        setRetrying(null)
        await onChanged()
    }

    async function handleReject({ reason, note }) {
        const photoId = rejectingSlot.photoId
        setRejectingSlot(null)
        const response = await reject(fetchResource, photoId, { reason, note })
        setRejectFailed(Boolean(response?.error))
        await onChanged()
    }

    async function handleRemove() {
        if (removing || !removingSlot) return
        setRemoving(true)
        const response = await removeShot(fetchResource, removingSlot.photoId)
        setRemoving(false)
        setRemovingSlot(null)
        setRemoveFailed(Boolean(response?.error))
        await onChanged()
    }

    // Pending again with its current version, so every tool below applies to it.
    async function handleUnapprove(photoId) {
        if (unapproving) return
        setUnapproveError(null)
        setUnapproving(photoId)
        const response = await unapprove(fetchResource, photoId)
        setUnapproving(null)
        if (response?.error) setUnapproveError(response.error)
        await onChanged()
    }

    // The version on screen, flipped left to right, as a new version to
    // approve. A 409 also refreshes: the shot moved on (a new version, or no
    // longer under review), and the page should show where it is now.
    async function handleMirror(slot) {
        if (mirroring) return
        setMirrorError(null)
        setMirroring(slot.photoId)
        const response = await mirrorShot(fetchResource, slot.photoId, slot.attempts)
        setMirroring(null)
        if (response?.error) setMirrorError({ photoId: slot.photoId, ...mirrorErrorMessage(response) })
        if (!response?.error || response.status === 409) await onChanged()
    }

    async function handleApproveAll() {
        setConfirmAll(false)
        setApproveErrors([])
        setApproving(true)
        // Sequential, and past failures: a 409 means someone approved it already.
        for (const photoId of ready) {
            await approveShown(photoId)
        }
        setApproving(false)
        await afterApprove()
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 4 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                <Link component={RouterLink} to='/photo-review'>← {t('photoReview.piece.back')}</Link>
                <Typography variant='h6' component='span'>{item.sku || sku}</Typography>
                {item.category && <Typography component='span' color='text.secondary'>{item.category}</Typography>}
                <Typography component='span'>{t('photoReview.piece.progress', { approved: badges.approved, total: badges.total })}</Typography>
                <Box sx={{ flex: 1 }} />
                {ready.length > 0 && (
                    <Button variant='contained' color='success' disabled={approving} onClick={() => setConfirmAll(true)}>
                        {t('photoReview.piece.approveAll', { count: ready.length })}
                    </Button>
                )}
            </Box>

            {/* A reopened piece: approving it again replaces its photos on the site. */}
            {item.republish && (
                <RepublishNotice sku={item.sku} republish={item.republish} fetchResource={fetchResource} onCancelled={onReopenCancelled} />
            )}

            {approveErrors.length > 0 && (
                <Alert severity='error' onClose={() => setApproveErrors([])}>
                    {approveErrors.map((message, index) => <div key={index}>{message}</div>)}
                </Alert>
            )}

            {rejectFailed && (
                <Alert severity='error' onClose={() => setRejectFailed(false)}>{t('photoReview.reject.failed')}</Alert>
            )}

            {unapproveError && (
                <Alert severity='error' onClose={() => setUnapproveError(null)}>{unapproveError}</Alert>
            )}

            {removeFailed && (
                <Alert severity='error' onClose={() => setRemoveFailed(false)}>{t('photoReview.remove.failed')}</Alert>
            )}

            {item.slots.map(slot => {
                const state = slotState(slot)
                const label = slotDisplayName(t, slot.slot)
                const busy = isBusy(slot)
                // Only a shot still waiting on a decision can be sent back for a
                // reshoot; one already approved, batching, or rejected cannot.
                const canReject = slot.status === 'pending_approval' || slot.status === 'uploaded'
                const rejectButton = canReject && (
                    <Button variant='outlined' color='error' disabled={busy || approving} onClick={() => setRejectingSlot(slot)}>
                        {t('photoReview.reject.button')}
                    </Button>
                )
                // Removing takes the shot out of the piece for good, so only a
                // shot not yet decided can go; the server refuses the rest.
                const canRemove = ['pending_approval', 'uploaded', 'rejected'].includes(slot.status)
                const removeButton = canRemove && (
                    <Button color='error' disabled={busy || approving} onClick={() => setRemovingSlot(slot)}>
                        {t('photoReview.remove.button')}
                    </Button>
                )
                // Download: the current version's full-size PNG, for any shot
                // with an edit (approved ones too). Its failure shows on the shot.
                const downloadButton = size => (
                    <DownloadButton
                        size={size}
                        target={downloadTarget(item.sku, slot)}
                        fetchResource={fetchResource}
                        label={t('photoReview.piece.download')}
                        onStart={() => setDownloadError(slot.photoId, null)}
                        onError={reason => setDownloadError(slot.photoId, reason || t('photoReview.piece.downloadFailed'))}
                    />
                )
                const currentKind = (slot.versions || []).find(version => version.current)?.kind
                const fromUpload = currentKind === 'manual'
                const sideActions = (rejectButton || removeButton) && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{rejectButton}{removeButton}</Box>
                )
                const actions = (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, alignItems: 'center' }}>
                        {state === 'ready' && (
                            <Button variant='contained' color='success' disabled={busy || approving} onClick={() => handleApprove(slot.photoId)}>
                                {t('photoReview.piece.approve')}
                            </Button>
                        )}
                        {state === 'ready' && (
                            <>
                                <Button variant='outlined' disabled={busy || approving} onClick={() => onReroll(slot)}>{t('photoReview.piece.reroll')}</Button>
                                <Button variant='outlined' disabled={busy || approving} onClick={() => setPromptSlot(slot)}>{t('photoReview.piece.editPrompt')}</Button>
                                <ShadowControl photoId={slot.photoId} fetchResource={fetchResource} disabled={busy || approving} fromUpload={fromUpload} onSubmit={settings => onReshadow(slot, settings)} />
                                <Button variant='outlined' disabled={busy || approving} onClick={() => setBrushSlot(slot)}>{t('photoReview.colorBrush.button')}</Button>
                                {/* One click, no dialog: Gemini often returns the piece flipped. Icon only, to keep the row short on a phone. */}
                                <Tooltip title={t('photoReview.mirror.hint')} describeChild>
                                    <span style={{ display: 'flex', alignSelf: 'stretch' }}>
                                        <Button variant='outlined' aria-label={t('photoReview.mirror.button')} disabled={busy || approving || Boolean(mirroring)}
                                            onClick={() => handleMirror(slot)} sx={{ minWidth: 0, px: 1 }}>
                                            {mirroring === slot.photoId ? <CircularProgress size={20} color='inherit' /> : <FlipIcon />}
                                        </Button>
                                    </span>
                                </Tooltip>
                                {/* The Photoshop round trip: out, then back in as "Your edit". */}
                                {downloadButton()}
                                <Button variant='outlined' startIcon={<UploadFileOutlinedIcon />} disabled={busy || approving} onClick={() => setReplaceSlot(slot)}>
                                    {t('photoReview.piece.replace')}
                                </Button>
                            </>
                        )}
                        {state === 'failed' && (
                            <Button variant='outlined' disabled={retrying === slot.photoId} onClick={() => handleRetry(slot.photoId)}>{t('photoReview.piece.retry')}</Button>
                        )}
                        {state === 'ready' && <Typography variant='caption' color='text.secondary'>{t('photoReview.piece.attempt', { n: slot.attempts })}</Typography>}
                        {/* ml: auto keeps Reject and Remove apart from Approve/Re-roll/Edit prompt, on the far side of the row. */}
                        {sideActions && <Box sx={{ ml: 'auto' }}>{sideActions}</Box>}
                        {/* Its own line: the strip of thumbnails opens below the buttons, full width. */}
                        {state === 'ready' && (
                            <VersionHistory sku={item.sku} slot={slot} fetchResource={fetchResource} disabled={busy || approving} onRestored={onChanged} />
                        )}
                    </Box>
                )

                return (
                    <Paper key={slot.photoId} data-testid={`shot-${slot.photoId}`} data-state={state} data-shot-card data-active={String(activeId === slot.photoId)}
                        onPointerDown={() => setActiveId(slot.photoId)}
                        variant='outlined'
                        sx={{ p: 2, borderColor: state === 'failed' ? 'error.main' : undefined, ...(activeId === slot.photoId && { outline: theme => `2px solid ${theme.palette.primary.main}`, outlineOffset: '2px' }) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>{label}</Typography>
                            {/* Still the exact image on the site (a reopened piece). */}
                            {currentKind === 'live' && <Chip size='small' variant='outlined' label={t('photoReview.versions.kinds.live')} />}
                            {state === 'approved' && <Chip size='small' color='success' label={t('photoReview.piece.approved')} />}
                            {state === 'approved' && (
                                <Button size='small' sx={{ ml: 'auto' }} onClick={() => toggleExpanded(slot.photoId)}>
                                    {t(expanded.has(slot.photoId) ? 'photoReview.piece.hidePhotos' : 'photoReview.piece.showPhotos')}
                                </Button>
                            )}
                            {state === 'approved' && downloadButton('small')}
                            {/* The server says whether it would refuse (a piece publishing or live). */}
                            {state === 'approved' && item.canSendBack === true && (
                                <Button size='small' variant='outlined' color='warning' disabled={Boolean(unapproving) || approving}
                                    onClick={() => handleUnapprove(slot.photoId)}>
                                    {t('photoReview.piece.unapprove')}
                                </Button>
                            )}
                            {state === 'failed' && <Chip size='small' color='error' label={t('photoReview.piece.failed')} />}
                            {state === 'rejected' && (
                                <Chip size='small' color='error' label={t('photoReview.piece.rejected', { reason: t(`photoReview.reject.reasons.${slot.rejection.reason}`) })} />
                            )}
                        </Box>
                        {/* On the shot itself: the refetch has put the new version right below. */}
                        {state === 'ready' && changedShots.has(slot.photoId) && (
                            <Alert severity='warning' sx={{ mb: 1 }}>{t('photoReview.piece.changed')}</Alert>
                        )}
                        {state === 'ready' && (
                            <>
                                <ShotCompare
                                    label={label}
                                    busy={busy}
                                    active={activeId === slot.photoId}
                                    {...compareSources(slot)}
                                />
                                {slot.hasError && <Alert severity='warning' sx={{ mt: 1 }}>{t('photoReview.piece.lastReeditFailed')}</Alert>}
                                {actions}
                            </>
                        )}
                        {mirrorError?.photoId === slot.photoId && state === 'ready' && (
                            <Alert severity='error' sx={{ mt: 1 }} onClose={() => setMirrorError(null)}>{t(mirrorError.key, mirrorError.values)}</Alert>
                        )}
                        {downloadErrors.has(slot.photoId) && (state === 'ready' || state === 'approved') && (
                            <Alert severity='error' sx={{ mt: 1 }} onClose={() => setDownloadError(slot.photoId, null)}>{downloadErrors.get(slot.photoId)}</Alert>
                        )}
                        {state === 'approved' && expanded.has(slot.photoId) && (
                            <ShotCompare
                                label={label}
                                active={activeId === slot.photoId}
                                {...compareSources(slot)}
                            />
                        )}
                        {(state === 'processing' || state === 'failed' || state === 'rejected') && (
                            <Box sx={{ maxWidth: 320 }}>
                                <img src={imageUrl(slot.photoId, 'raw', slot.rawVersion, 'preview')} alt={t('photoReview.piece.originalAlt', { label })} style={{ width: '100%', borderRadius: 4 }} />
                            </Box>
                        )}
                        {state === 'processing' && <Typography color='text.secondary'>{t('photoReview.piece.processing')}</Typography>}
                        {state === 'processing' && sideActions && <Box sx={{ mt: 1 }}>{sideActions}</Box>}
                        {state === 'failed' && (
                            <>
                                <FailedError photoId={slot.photoId} fetchResource={fetchResource} />
                                {actions}
                            </>
                        )}
                        {state === 'rejected' && (
                            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {slot.rejection.note && <Typography variant='body2' color='text.secondary'>{slot.rejection.note}</Typography>}
                                {slot.rejection.rejectedAt && <Typography variant='caption' color='text.secondary'>{formatDate(slot.rejection.rejectedAt)}</Typography>}
                                {removeButton && <Box>{removeButton}</Box>}
                            </Box>
                        )}
                    </Paper>
                )
            })}

            <Sheet
                open={confirmAll}
                onClose={() => setConfirmAll(false)}
                title={t('photoReview.piece.approveAllTitle', { count: ready.length, sku: item.sku })}
                actions={(
                    <>
                        <Button onClick={() => setConfirmAll(false)}>{t('photoReview.piece.cancel')}</Button>
                        <Button variant='contained' color='success' onClick={handleApproveAll}>{t('photoReview.piece.approveAllConfirm', { count: ready.length })}</Button>
                    </>
                )}
            />

            {brushSlot && (
                <ColorBrush
                    open
                    slot={brushSlot}
                    label={slotDisplayName(t, brushSlot.slot)}
                    fetchResource={fetchResource}
                    onClose={() => setBrushSlot(null)}
                    onApply={masks => { const target = brushSlot; setBrushSlot(null); onColorMatch(target, masks) }}
                />
            )}

            {replaceSlot && (
                <ReplaceDialog
                    open
                    slot={replaceSlot}
                    label={slotDisplayName(t, replaceSlot.slot)}
                    fetchResource={fetchResource}
                    onClose={() => setReplaceSlot(null)}
                    onReplaced={onChanged}
                />
            )}

            {promptSlot && (
                <PromptDrawer
                    open
                    photoId={promptSlot.photoId}
                    shotLabel={slotDisplayName(t, promptSlot.slot)}
                    fetchResource={fetchResource}
                    onClose={() => setPromptSlot(null)}
                    onSend={prompt => onReeditWithPrompt(promptSlot, prompt)}
                />
            )}

            <Sheet
                open={Boolean(removingSlot)}
                onClose={() => { if (!removing) setRemovingSlot(null) }}
                title={removingSlot ? t('photoReview.remove.title', { label: slotDisplayName(t, removingSlot.slot), sku: item.sku }) : ''}
                actions={(
                    <>
                        <Button disabled={removing} onClick={() => setRemovingSlot(null)}>{t('photoReview.remove.cancel')}</Button>
                        <Button variant='contained' color='error' disabled={removing} onClick={handleRemove}>{t('photoReview.remove.confirm')}</Button>
                    </>
                )}
            >
                <Typography>{t('photoReview.remove.body')}</Typography>
            </Sheet>

            {rejectingSlot && (
                <RejectDialog
                    open
                    shotLabel={slotDisplayName(t, rejectingSlot.slot)}
                    onConfirm={handleReject}
                    onCancel={() => setRejectingSlot(null)}
                />
            )}
        </Box>
    )
}
